import { type ComponentProps } from 'react'
import ReactMarkdown from 'react-markdown'
import { Modal } from '@atrvd/saas-ui'
import { getAppVersion, getBuildDate, getChannel, formatBuildDate } from '@/lib/build-info'
import { useReleaseNotes } from '@/hooks/useReleaseNotes'

/**
 * Compact styling for the AI-generated note markdown (headings, bullet lists,
 * issue links). Links open in a new tab (noopener) — the dialog must survive
 * the click. Ported from the ledger-classifier "What's new" panel so DCP's
 * changelog renders headings/bullets/links properly instead of as raw text.
 */
const MARKDOWN_COMPONENTS: ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h4 className="mt-2 text-xs font-semibold first:mt-0">{children}</h4>,
  h2: ({ children }) => <h4 className="mt-2 text-xs font-semibold first:mt-0">{children}</h4>,
  h3: ({ children }) => <h4 className="mt-2 text-xs font-semibold first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="mt-1.5 first:mt-0">{children}</p>,
  ul: ({ children }) => <ul className="mt-1.5 list-disc space-y-1 pl-4 first:mt-0">{children}</ul>,
  ol: ({ children }) => (
    <ol className="mt-1.5 list-decimal space-y-1 pl-4 first:mt-0">{children}</ol>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:underline"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded bg-gray-100 px-1 font-mono text-[11px]">{children}</code>
  ),
}

/** Props for {@link AboutBuildModal}. */
interface AboutBuildModalProps {
  /** Whether the dialog is currently open (controlled by the parent). */
  isOpen: boolean
  /** Called when the dialog requests to close. */
  onClose: () => void
}

/**
 * Normalizes the build-time app version into the `vX.Y.Z[-suffix]` cap the
 * `release-notes` function expects, or null when there is no valid semver tag
 * (a local `dev` build). Null caps nothing — and skips the 400 an invalid
 * version string would otherwise trigger in the function.
 */
function toVersionCap(raw: string): string | null {
  if (!raw || raw === 'dev') return null
  const withV = `v${raw.replace(/^v/, '')}`
  return /^v\d+\.\d+\.\d+(?:-[A-Za-z0-9.-]{1,64})?$/.test(withV) ? withV : null
}

/**
 * "About this build" dialog (DCP-294).
 *
 * A controlled modal showing the build-time app version, build date and release
 * channel, plus a lazily-fetched "What's new" list of recent Linear releases
 * (newest first, capped at this build's version). Rendered by {@link UserMenu}
 * OUTSIDE the dropdown so it survives the dropdown closing when its trigger is
 * clicked.
 *
 * Release notes come from the `release-notes` Supabase edge function (recent
 * mode) via {@link useReleaseNotes} — fetched on first open, then cached by
 * React Query so reopening doesn't refetch within the TTL. Each release renders
 * its markdown note with {@link MARKDOWN_COMPONENTS}; a release without a note
 * shows a quiet "no notes" line.
 */
export function AboutBuildModal({ isOpen, onClose }: AboutBuildModalProps) {
  const version = getAppVersion()
  const channel = getChannel()
  const formattedDate = formatBuildDate(getBuildDate())

  const versionCap = toVersionCap(version)
  const { data: releases, isLoading, isError } = useReleaseNotes(versionCap, isOpen)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="About this build" size="md">
      <div className="space-y-4">
        {/* Build metadata */}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-gray-500">Version</dt>
          <dd className="text-gray-900">{version}</dd>
          <dt className="text-gray-500">Build date</dt>
          <dd className="text-gray-900">{formattedDate || 'Unknown'}</dd>
          <dt className="text-gray-500">Channel</dt>
          <dd className="text-gray-900">{channel}</dd>
        </dl>

        {/* What's new — recent Linear releases (newest first). */}
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-900">What&apos;s new</h3>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading release notes…</p>
          ) : isError ? (
            <p className="text-sm text-gray-500">No changelog available.</p>
          ) : releases && releases.length > 0 ? (
            // No own max-height / overflow on this list (DCP-313): the saas-ui
            // Modal body is already a scroll container (max-h-[60vh]
            // overflow-y-auto), so a nested one produced a second, inner
            // scrollbar. Letting the list flow makes the modal body the single
            // scroller.
            <div className="space-y-3.5 text-sm text-gray-700">
              {releases.map((release, i) => (
                <div
                  key={release.url ?? `${release.version}-${i}`}
                  className={i ? 'border-t border-gray-100 pt-3.5' : ''}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-gray-900">{release.version}</span>
                    {release.completedAt && formatBuildDate(release.completedAt) && (
                      <span className="text-[11px] text-gray-400">
                        {formatBuildDate(release.completedAt)}
                      </span>
                    )}
                  </div>
                  {release.notes?.trim() ? (
                    <div className="mt-1 text-xs">
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {release.notes}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400">No notes for this release.</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No release notes yet.</p>
          )}
        </div>
      </div>
    </Modal>
  )
}
